import React, { useEffect, useRef } from "react";
import styles from "./Weekend.module.css";
import mainStyles from "racing-renderings-library/src/Components/Charts.module.css";
import {
  createHiDPICanvas,
  getLineObj,
  getRowsPattern,
  lineSegmentPoint,
  msToTime,
  onResize,
  ratio,
  updSTATE,
} from "racing-renderings-library/src/canvasesUtils";
import { getCoords, getResized, getWidths, recordWidget } from "racing-renderings-library/src/utils";
import ControlButtons from "racing-renderings-library/src/Components/ControlBtns/ControlBtns";
import ShareBtns from "racing-renderings-library/src/Components/ShareBtns/ShareBtns";
import DataBy from "racing-renderings-library/src/Components/DataBy/DataBy";
import legendImg from "racing-renderings-library/assets/images/chart/png/legend-weekend.png";
import { getGridPattern } from "../../canvasesUtils";
import CustomizeBlock from "../CustomizeBlock/CustomizeBlock";

const Weekend = ({ data, color, theme, icons, logo, ...props }) => {
  const playBtnRef = useRef();
  const speedUpBtnRef = useRef();
  const speedDownBtnRef = useRef();
  const moveBackRef = useRef();
  const moveForwardRef = useRef();
  const legendImgRef = useRef();

  const canvasesDivRef = useRef();
  const rowsRef = useRef();
  const redHeadRef = useRef();
  const leftRef = useRef();
  const fieldRef = useRef();
  const rightRef = useRef();
  const fullStageInfoRef = useRef();
  const dataByRef = useRef();
  const ctxs = {};

  const recordingSpanRef = useRef();

  const updDriverPresentState = (timePassed) => {
    const progress = timePassed / STATE.driverPresent.animDuration;
    if (STATE.driverPresent.progress !== progress) {
      STATE.driverPresent.progress = progress;
      STATE.driverPresent.curName = driversCount * progress;

      if (moveBackRef.current !== null) moveBackRef.current.disabled = progress === 0;

      drawLeftCol();
    }
  };
  const updTrafficLightState = (timePassed) => {
    STATE.trafficLight.progress = timePassed / STATE.trafficLight.animDuration;
  };
  const updRideState = (timePassed) => {
    const progress = timePassed / STATE.ride.animDuration;
    const length = size.stageWidth * data.stages.length;
    STATE.ride.progress = progress;
    STATE.ride.x = Math.min(length * progress, columnsX.stages[columnsX.stages.length - 1]);
  };
  const updResultsPresentState = (timePassed) => {
    const progress = timePassed / STATE.resultsPresent.animDuration;
    STATE.resultsPresent.progress = progress;
    if (moveForwardRef.current !== null) moveForwardRef.current.disabled = progress === 1;
    if (progress === 1) {
      if (playBtnRef.current !== null) playBtnRef.current.checked = false;
      STATE.playing = false;
    }
  };
  const resetState = (timePassed = 0) => {
    STATE.timePassed = timePassed;
    updSTATE(STATE.timePassed, STATE);
    lastFrameDate = Date.now();
  };
  const setFullAnimDur = () => {
    let fullAnimDur = 0;
    for (const phase of STATE.phases) {
      fullAnimDur += STATE[phase[0]].animDuration;
    }
    STATE.animDuration = fullAnimDur;
  };
  const STATE = {
    playing: true,
    timePassed: 0,
    progress: 0,
    animDuration: 0,
    phases: [
      ["driverPresent", updDriverPresentState],
      ["trafficLight", updTrafficLightState],
      ["ride", updRideState],
      ["resultsPresent", updResultsPresentState],
    ],
    driverPresent: {
      animDuration: Math.min(data.drivers.length * 250, 5000), // *250
      progress: 0,
      curName: 0,
    },
    trafficLight: {
      animDuration: 500,
      progress: 0,
    },
    ride: {
      animDuration: data.stages.length * 1000,
      progress: 0,
      x: 0,
    },
    resultsPresent: {
      animDuration: 500,
      progress: 0,
    },
  };

  const driversCount = data.drivers.length;
  const stagesCount = data.stages.length;
  const rowsCount = data.drivers.length;
  const finishNum = data.stages.find((s) => s.isFinal)?.number;
  let mainColor = theme?.mainColor ?? color;
  let BGColor = theme?.BGColor ?? "#ffffff";
  let textColor = theme?.textColor ?? "#333333";
  let headerColor = theme?.headerColor ?? "#ffffff";
  let ffl = (theme?.font ?? "Gibson-Light") + ", sans-serif";
  let ffr = (theme?.font ?? "Gibson-regular") + ", sans-serif";
  const size = {};
  const selectedDrivers = [];
  for (let i = 1; i <= driversCount; i++) selectedDrivers.push(i);
  let delY = 30;
  const getCheckSize = () => Math.floor(delY / 2);
  let checkSize = getCheckSize();
  const getFontSize = () => Math.floor(delY / 2);
  let fontSize = getFontSize();
  const getIconSize = () => Math.round(delY * 0.6);
  let iconSize = getIconSize();
  const getFlagSize = () => ({
    width: Math.round(delY / 1.2),
    height: Math.round(delY / 2),
  });
  let flagSize = getFlagSize();
  const getCupSize = () => Math.round(delY * 0.4);
  let cupSize = getCupSize();

  let speed = 0;
  let fullStageInfo = undefined;
  let isLegendShown = false;
  let lastFrameDate, nextFrameID;
  let marginLeft, marginRight;
  let headHeight, lColWidth, rColWidth;
  let columnsX, grid, shadows;

  const getShadows = (w = 20) => {
    const getShadow = (isLeft) => {
      const shadow = document.createElement("canvas");
      shadow.width = w;
      shadow.height = size.height - headHeight;
      const ctx = shadow.getContext("2d");
      const gradient = ctx.createLinearGradient(0, 10, w, 10);
      gradient.addColorStop(isLeft ? 0 : 1, "rgba(0,0,0,0.2)");
      gradient.addColorStop(isLeft ? 1 : 0, "rgba(0,0,0,0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, size.height - headHeight);
      return shadow;
    };
    return { left: getShadow(false), right: getShadow(true) };
  };
  const getColumnsX = () => {
    const ww = size.windowWidth;
    const stages = [20];
    for (let i = 1; i < stagesCount; i++) {
      stages.push(i * size.stageWidth + 20);
    }
    return {
      lpos: ww > 1000 ? 12 : -100,
      lcheck: ww > 1000 ? 50 : 10,
      lflag: ww > 1000 ? 75 : -100,
      lname: ww > 1000 ? 110 : 35,

      stages,

      // finishX - (finishX - gridX) / 2
      // raceDuration: lColWidth + fw - ((lColWidth + fw) - (lColWidth + fw / 6 * 4)) / 2,

      rname: ww > 700 ? 10 : 100,
      rpos: ww > 700 ? 70 : 15,
      rpts: 120,
      rmove: 170,
      rnote: 230,
    };
  };
  const getGrid = (rectSize = 5, width = 10, height = delY * rowsCount) => {
    const grid = document.createElement("canvas");
    grid.width = width;
    grid.height = height;
    const ctx = grid.getContext("2d");

    ctx.fillStyle = ctx.createPattern(getGridPattern(rectSize, BGColor), "repeat");
    ctx.fillRect(0, 0, width, height);

    return grid;
  };
  const setIncludedDriverAlpha = (ctx, pos, value = 0.4) => {
    if (selectedDrivers.length === 0) ctx.globalAlpha = value;
    else if (selectedDrivers.length < driversCount) {
      if (selectedDrivers.includes(pos)) ctx.globalAlpha = 1;
      else ctx.globalAlpha = value;
    }
  };
  const presentSettings = (ctx, position, xOffset) => {
    let resultXOffset = 0;
    if (STATE.driverPresent.curName < position - 1) {
      ctx.globalAlpha = 0;
      resultXOffset = xOffset;
    } else if (STATE.driverPresent.curName < position) {
      ctx.globalAlpha = 1 - (position - STATE.driverPresent.curName);
      resultXOffset = xOffset * (position - STATE.driverPresent.curName);
    }

    return resultXOffset;
  };
  const deselectDriver = (gridPos) => {
    for (let i = 0; i < selectedDrivers.length; i++) {
      if (selectedDrivers[i] === gridPos) selectedDrivers.splice(i, 1);
    }
  };

  // Customization
  const parseCustomizeBlock = (type, value) => {
    switch (type) {
      case "mainColor":
        changeMainColor(value);
        break;
      case "headerColor":
        changeHeaderColor(value);
        break;
      case "BGColor":
        changeBGColor(value);
        break;
      case "textColor":
        changeTextColor(value);
        break;
      case "FontFamily":
        changeFontFamily(value);
        break;
      case "logo":
        logo = value;
        break;
      default:
        break;
    }
  };
  const changeMainColor = (color) => {
    mainColor = color;
    dataByRef.current.style.background = color;
    !STATE.playing && forcedRedraw();
  };
  const changeHeaderColor = (color) => {
    headerColor = color;
    !STATE.playing && forcedRedraw();
  };
  const changeBGColor = (color) => {
    BGColor = color;
    grid = getGrid();
    forcedRedraw();
  };
  const changeTextColor = (color) => {
    textColor = color;
    !STATE.playing && forcedRedraw();
  };
  const changeFontFamily = (font) => {
    ffl = font + ", sans-serif";
    ffr = font + ", sans-serif";
    setContexts();
    setTimeout(forcedRedraw, 200);
  };
  const getThemeData = () => {
    return {
      mainColor,
      headerColor,
      BGColor,
      textColor,
      logo,
      font: ffr.split(", ")[0].replace(" ", "+"),
    };
  };

  const clickOnCanvas = (e) => {
    const canvasPos = getCoords(canvasesDivRef);
    const headerPos = getCoords(redHeadRef);

    const x = e.pageX - Math.ceil(canvasPos.left),
      y = e.pageY - Math.ceil(canvasPos.top);

    const hx = e.pageX - Math.ceil(headerPos.left),
      hy = e.pageY - Math.ceil(headerPos.top);

    if (x < lColWidth && hy > headHeight) clickOnDriver(y);
    if (hy < headHeight - 5 && hy > headHeight - 20 - iconSize) clickOnStageInfo(hx);
    const checkY = headHeight - 8;
    if (hx > columnsX.lcheck && hx < columnsX.lcheck + checkSize && hy > checkY - checkSize && hy < checkY)
      clickOnMainCheckBox();
  };
  const clickOnMainCheckBox = () => {
    if (selectedDrivers.length < driversCount) {
      selectedDrivers.length = 0;
      for (let i = 1; i <= driversCount; i++) selectedDrivers.push(i);
    } else selectedDrivers.length = 0;
    forcedRedraw();
  };
  const clickOnDriver = (y) => {
    const pos = Math.ceil((y - headHeight) / delY);
    if (selectedDrivers.includes(pos)) {
      deselectDriver(pos);
    } else selectedDrivers.push(pos);

    forcedRedraw();
  };
  const clickOnStageInfo = (x) => {
    x = x - lColWidth + getCenterOffset();
    for (const stage of data.stages) {
      if (stage.isFinal) continue;
      const stageX = columnsX.stages[stage.number - 1];
      if (x < stageX + iconSize / 2 && x > stageX - iconSize / 2) {
        if (STATE.playing) onClickPlay();
        if (fullStageInfo === stage.number) {
          fullStageInfo = undefined;
        } else {
          fullStageInfo = stage.number;
        }
        forcedRedraw();
        break;
      }
    }
  };
  const getScreen = () => {
    const screen = document.createElement("canvas");
    screen.width = size.width;
    screen.height = size.height;
    const ctx = screen.getContext("2d");
    ctx.drawImage(rowsRef.current, 0, 0, size.width, size.height);
    ctx.drawImage(redHeadRef.current, 0, 0, size.width, headHeight);
    ctx.drawImage(leftRef.current, 0, 0, size.width, size.height);
    ctx.drawImage(fieldRef.current, 0, 0, size.width, size.height);
    ctx.drawImage(rightRef.current, 0, 0, size.width, size.height);
    return screen;
  };
  const downloadScreenshot = () => {
    const screen = getScreen();
    const link = document.createElement("a");
    const image = screen.toDataURL("image/jpeg", 1.0).replace("image/png", "image/octet-stream");
    link.download = "race.jpg";
    link.href = image;
    link.click();
    link.remove();
  };
  const showLegend = () => {
    isLegendShown = !isLegendShown;
    legendImgRef.current.style.display = isLegendShown ? "block" : "none";
  };
  const updScreen = (ctx, topOffset = 0) => {
    ctx.drawImage(rowsRef.current, 0, topOffset, size.width, size.height);
    ctx.drawImage(fieldRef.current, 0, topOffset, size.width, size.height);
    ctx.drawImage(leftRef.current, 0, topOffset, size.width, size.height);
    ctx.drawImage(rightRef.current, 0, topOffset, size.width, size.height);
    ctx.drawImage(redHeadRef.current, 0, topOffset, size.width, headHeight);
    return STATE.progress;
  };
  const record = async () => {
    recordWidget({
      beforeRecord: setRecordSettings,
      afterRecord: resetRecordSettings,
      canvasesBlock: canvasesDivRef.current,
      span: recordingSpanRef.current,
      countryFlag: data.countryFlag,
      title: data.title.shortTitle,
      logoSrc: logo,
      BGColor,
      mainColor,
      ffl,
      replay,
      updCanvas: (ctx, head, footer) => updScreen(ctx, head, footer),
    });
  };

  const updVariables = () => {
    size.windowWidth = getWidths().windowWidth;
    size.visibleWidth = getWidths().visibleWidth;

    marginLeft = size.windowWidth > 1000 ? 15 : 0;
    marginRight = 1; //Чтобы не появлялась горизонтальная прокрутка

    lColWidth = size.windowWidth > 1000 ? 170 : 100;
    rColWidth = size.windowWidth > 1000 ? 400 : size.windowWidth > 700 ? 110 : 50;

    size.widthWithDataBy = size.visibleWidth - marginLeft - marginRight;
    size.width = size.widthWithDataBy - (size.windowWidth > 1000 ? 50 : 0);

    size.fieldWidth = size.width - lColWidth - rColWidth;
    let stageWidth = size.fieldWidth / stagesCount;
    if (stagesCount < 2) stageWidth = size.fieldWidth;
    if (stagesCount > 10) stageWidth = size.fieldWidth / 10;
    size.stageWidth = stageWidth;

    canvasesDivRef.current.style.width = size.widthWithDataBy + "px";
    canvasesDivRef.current.style.marginLeft = marginLeft + "px";
    canvasesDivRef.current.style.height = size.height + "px";
    // canvasesDivRef.current.style.height = (size.height + (size.windowWidth<1000&&50)) + 'px'

    columnsX = getColumnsX();

    rightRef.current.style.top = headHeight + "px";
    rightRef.current.style.left = size.width - rColWidth + "px";
    fieldRef.current.style.top = headHeight + "px";
    fieldRef.current.style.left = lColWidth + "px";

    setContexts();
    updSTATE(STATE.timePassed, STATE);
  };
  const updWidth = () => {
    if (size.visibleWidth === getWidths().visibleWidth) return;
    updVariables();
  };
  const updHeight = () => {
    headHeight = 70;
    delY = 30;
    size.height = headHeight + delY * rowsCount;
    size.fieldHeight = delY * rowsCount;
    grid = getGrid();
    flagSize = getFlagSize();
    iconSize = getIconSize();
    cupSize = getCupSize();
    shadows = getShadows();
    updVariables();
  };
  const setRecordSettings = (sv = { w: 1920, h: 1080 }) => {
    size.width = sv.w;
    size.height = sv.h;
    delY = size.height / (driversCount + 1.7);
    headHeight = delY * 1.7;
    size.fieldHeight = size.height - headHeight;

    flagSize = getFlagSize();
    iconSize = getIconSize();
    checkSize = getCheckSize();
    fontSize = getFontSize();
    cupSize = getCupSize();

    columnsX.lpos = 12;
    columnsX.lcheck = -100;
    columnsX.lflag = columnsX.lpos + fontSize * 3;
    columnsX.lname = columnsX.lflag + flagSize.width + 10;

    lColWidth = columnsX.lname + fontSize * 4;
    rColWidth = fontSize * 28;

    size.fieldWidth = size.width - lColWidth - rColWidth;

    const stages = [];
    const stageWidth = size.fieldWidth / (stagesCount - 1);
    for (let i = 0; i < stagesCount; i++) stages.push(lColWidth + i * stageWidth);
    columnsX.stages = stages;

    columnsX.rname = stages[stages.length - 1] + delY * 2;
    columnsX.rpos = columnsX.rname + fontSize * 4;
    columnsX.rpts = columnsX.rpos + fontSize * 3;
    columnsX.rmove = columnsX.rpts + fontSize * 3;
    columnsX.rnote = columnsX.rmove + fontSize * 4;

    size.widthWithDataBy = size.width + 50;
    canvasesDivRef.current.style.width = size.widthWithDataBy + "px";
    canvasesDivRef.current.style.marginLeft = "15px";
    canvasesDivRef.current.style.height = size.height + "px";

    grid = getGrid();
    shadows = getShadows();
    setContexts(false);
    for (const ctx of Object.keys(ctxs)) ctxs[ctx].font = `600 ${fontSize}px ${ffl}`;
    drawRows();
    forcedRedraw();
  };
  const resetRecordSettings = () => {
    updHeight();
    updWidth();

    flagSize = getFlagSize();
    iconSize = getIconSize();
    checkSize = getCheckSize();
    fontSize = getFontSize();
    cupSize = getCupSize();
    grid = getGrid();
    shadows = getShadows();
    setContexts();

    forcedRedraw();
  };
  const changeSpeed = (isFaster) => {
    const step = 10;
    const max = 100;
    const min = -20;

    speed += isFaster ? step : -step;
    speed = Math.min(max, Math.max(min, speed));

    speedUpBtnRef.current.disabled = speed === max;
    speedDownBtnRef.current.disabled = speed === min;
  };
  const rewind = (isForward = true) => {
    if (isForward && STATE.resultsPresent.progress === 1) return;
    const timeDel = STATE.ride.animDuration / 6;
    STATE.timePassed += isForward ? timeDel : -timeDel;
    if (STATE.timePassed < 0) STATE.timePassed = 0;
    if (!STATE.playing) {
      updSTATE(STATE.timePassed, STATE);
      forcedRedraw();
    }
  };
  const getCenterOffset = () => {
    let offset = 0;
    if (STATE.ride.x > size.fieldWidth / 2) {
      if (STATE.ride.x <= size.stageWidth * stagesCount - size.fieldWidth / 2) {
        offset = STATE.ride.x - size.fieldWidth / 2;
      } else {
        offset = size.stageWidth * stagesCount - size.fieldWidth;
      }
    }
    return offset;
  };
  const translateField = (ctx) => {
    ctx.translate(-getCenterOffset(), 0);
  };

  const drawRows = (ctx = ctxs.rowsCTX, x = 0, y = headHeight, w = size.width, h = size.fieldHeight) => {
    const row = getRowsPattern(delY, BGColor);
    for (let i = 0; i < h / delY / 2; i++) {
      ctx.drawImage(row, x, y + i * delY * 2, w, delY * 2);
    }
  };
  const drawRedHead = () => {
    const ctx = ctxs.redHeadCTX;
    ctx.globalAlpha = 1;

    ctx.clearRect(0, 0, size.width, size.height);
    ctx.fillStyle = mainColor;

    drawHeadStagesLine();

    ctx.fillRect(0, 0, lColWidth, headHeight);
    ctx.fillRect(size.fieldWidth + lColWidth, 0, rColWidth, headHeight);

    let y = headHeight - 10;
    if (size.windowWidth < 600 && fullStageInfo !== undefined && fullStageInfo !== "GRID") y -= 15;
    ctx.fillStyle = headerColor;
    ctx.strokeStyle = headerColor;
    ctx.lineWidth = 2;
    ctx.fillText("POS", columnsX.lpos, y);
    // Чекбокс
    ctx.beginPath();
    ctx.moveTo(columnsX.lcheck, y - checkSize + 2);
    ctx.lineTo(columnsX.lcheck + checkSize, y - checkSize + 2);
    ctx.lineTo(columnsX.lcheck + checkSize, y + 2);
    ctx.lineTo(columnsX.lcheck, y + 2);
    ctx.lineTo(columnsX.lcheck, y - checkSize + 2);
    ctx.stroke();
    if (selectedDrivers.length === driversCount) {
      ctx.beginPath();
      ctx.moveTo(columnsX.lcheck + 2, y - checkSize / 2 + 1);
      ctx.lineTo(columnsX.lcheck + checkSize / 2.5, y - 2);
      ctx.lineTo(columnsX.lcheck + checkSize - 2, y - checkSize / 2 - 3);
      ctx.stroke();
    }
    ctx.fillText("DRIVER", size.windowWidth > 1000 ? columnsX.lflag : columnsX.lname, y);

    ctx.textAlign = "start";
    ctx.fillText("DRIVER", size.width - rColWidth + columnsX.rname, y);
    ctx.fillText("POS", size.width - rColWidth + columnsX.rpos, y);
    ctx.fillText("PTS", size.width - rColWidth + columnsX.rpts, y);
    ctx.fillText("MOVE", size.width - rColWidth + columnsX.rmove, y);
    ctx.fillText("NOTE", size.width - rColWidth + columnsX.rnote, y);
  };
  const drawHeadStagesLine = () => {
    const ctx = ctxs.redHeadCTX;
    ctx.fillRect(lColWidth - 5, 0, size.fieldWidth + 10, headHeight);
    translateField(ctx);
    ctx.save();
    ctx.fillStyle = headerColor;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.font = `700 ${fontSize}px ${ffr}`;

    let y = headHeight - iconSize;
    if (fullStageInfo !== undefined) y -= 15;
    for (const stage of data.stages) {
      const x = lColWidth + columnsX.stages[stage.number - 1];
      if (STATE.ride.x + size.fieldWidth + lColWidth < x) continue;
      if (STATE.ride.x - size.fieldWidth > x) continue;
      ctx.fillText(stage.short, x, y - 10);

      if (stage.isFinal) continue;

      if (fullStageInfo === stage.number) {
        ctx.drawImage(icons.whiteMinus, x - iconSize / 2, y - 5, iconSize, iconSize);
        ctx.save();
        const hy = headHeight - 2;
        const driverX = size.windowWidth > 600 ? x - flagSize.width / 2 : size.windowWidth - 260;
        const posX = driverX + 85;
        const timeX = posX + 30;
        const lapsX = timeX + 90;
        ctx.textAlign = "start";
        ctx.textBaseline = "bottom";
        ctx.font = `500 ${fontSize - 4}px ${ffl}`;
        ctx.fillText("DRIVER", driverX, hy);
        ctx.fillText("POS", posX, hy);
        ctx.fillText("TIME", timeX, hy);
        ctx.fillText("LAPS", lapsX, hy);
        ctx.restore();
      } else ctx.drawImage(icons.whitePlus, x - iconSize / 2, y - 5, iconSize, iconSize);
    }

    ctx.restore();
    ctx.setTransform(STATE.recording ? 1 : ratio.value, 0, 0, STATE.recording ? 1 : ratio.value, 0, 0);
    ctx.drawImage(shadows.right, lColWidth, 0, 20, headHeight);
    ctx.drawImage(shadows.left, size.width - rColWidth - 20, 0, 20, headHeight);
  };
  const drawLeftCol = () => {
    const ctx = ctxs.leftCTX;
    ctx.clearRect(0, 0, size.width, size.height);
    ctx.fillStyle = textColor;
    ctx.strokeStyle = textColor;
    ctx.textBaseline = "middle";

    for (let i = 0; i < driversCount; i++) {
      const driver = data.drivers[i];
      const position = driver.presentPos;
      const y = Math.round(headHeight + delY / 2 + delY * (position - 1));

      ctx.globalAlpha = 1;
      setIncludedDriverAlpha(ctx, position);
      // Установки для появления
      let xOffset = Math.round(presentSettings(ctx, position, -100));

      // Чекбокс
      ctx.fillText(position, columnsX.lpos + xOffset, y);
      if (selectedDrivers.includes(position)) {
        ctx.beginPath();
        ctx.moveTo(columnsX.lcheck + 2 + xOffset, y);
        ctx.lineTo(columnsX.lcheck + checkSize / 2.5 + xOffset, y + checkSize / 2 - 3);
        ctx.lineTo(columnsX.lcheck + checkSize - 2 + xOffset, y - checkSize / 2 + 4);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(columnsX.lcheck + xOffset, y - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + checkSize + xOffset, y - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + checkSize + xOffset, y + checkSize - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + xOffset, y + checkSize - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + xOffset, y - checkSize / 2);
      ctx.stroke();
      // флаг
      if (driver.flag?.img !== undefined) {
        ctx.drawImage(
          driver.flag.img,
          columnsX.lflag + xOffset,
          Math.round(y - flagSize.height / 2),
          flagSize.width,
          flagSize.height
        );
      } else ctx.fillText(driver.flag.code, columnsX.lflag + xOffset, y);
      // Имя
      ctx.font = `600 ${fontSize}px ${ffr}`;
      if (driver.code === undefined) ctx.fillText(driver.name, columnsX.lname + xOffset, y);
      else ctx.fillText(driver.code, columnsX.lname + xOffset, y);
      ctx.font = `600 ${fontSize}px ${ffl}`;
    }
  };
  const fillLineWithBorderIfNeed = (ctx, color) => {
    if (color === "255,255,255") {
      ctx.save();
      ctx.lineWidth = 8;
      ctx.strokeStyle = "#000000";
      ctx.stroke();
      ctx.lineWidth = 6;
      ctx.strokeStyle = "#FFFFFF";
      ctx.stroke();
      ctx.restore();
    } else ctx.stroke();
  };
  const drawLines = () => {
    const ctx = ctxs.fieldCTX;
    const arcR = size.windowWidth > 600 ? 6 : 3;

    const drawLine = (x1, y1, x2, y2, color) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      fillLineWithBorderIfNeed(ctx, color);
      // ctx.stroke()
    };
    const drawArc = (x, y, color, isDash) => {
      const isWhite = color === "255,255,255";
      ctx.save();
      ctx.fillStyle = isWhite ? "#000000" : "#ffffff";
      ctx.beginPath();
      ctx.arc(x, y, arcR + (isWhite ? 1 : 2), 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = "rgb(" + color + ")";
      ctx.strokeStyle = "rgb(" + color + ")";
      ctx.beginPath();
      ctx.arc(x, y, arcR, 0, 2 * Math.PI);
      if (isDash) {
        ctx.setLineDash([]);
        ctx.lineWidth = 2;
        ctx.stroke();
      } else ctx.fill();
      ctx.restore();
    };

    ctx.lineWidth = size.windowWidth > 600 && rowsCount < 25 ? 6 : 3;
    ctx.globalAlpha = 1;

    for (let i = 0; i < driversCount; i++) {
      const driver = data.drivers[i];

      ctx.fillStyle = `rgb(${driver.color})`;
      ctx.strokeStyle = `rgba(${driver.color}, 0.4)`;

      setIncludedDriverAlpha(ctx, driver.presentPos, 0.1);

      const baseY = delY / 2;
      const carH = Math.round(delY * 0.6);
      const carW = getResized({ img: driver.miniCar, h: carH });

      //TODO: fixme
      if (stagesCount === 1) {
        const stage = data.stages[0];
        const dsi = driver.stages.find((s) => s.number === stage.number);
        if (dsi === undefined) continue;
        const sx = columnsX.stages[0];
        const y = baseY + delY * (dsi.position - 1);

        // Кружочки \ новые гонщики
        drawArc(sx, y, driver.color, dsi.absent);
        // Машинка
        ctx.drawImage(driver.miniCar, sx, y - carH / 2, carW, carH);
        ctx.setLineDash([]);
      }

      for (let k = 0; k < stagesCount - 1; k++) {
        const stage = data.stages[k];
        const sx = columnsX.stages[stage.number - 1];
        const dsi = driver.stages.find((s) => s.number === stage.number);
        if (dsi === undefined) continue;
        const y = baseY + delY * (dsi.position - 1);
        if (STATE.ride.x <= columnsX.stages[0] && k === 0) ctx.drawImage(driver.miniCar, sx, y - carH / 2, carW, carH);
        if (STATE.ride.x <= sx) continue;
        if (STATE.ride.x - size.fieldWidth - size.stageWidth >= sx) continue;

        const nextStage = data.stages.find((s) => s.number === stage.number + 1);

        let nextX = columnsX.stages[nextStage.number - 1];
        if (STATE.ride.x <= nextX) nextX = nextX - (nextX - STATE.ride.x);
        let nextStageY;
        // driver next stage ind
        const dnsi = driver.stages.find((s) => s.number === nextStage.number);
        if (dnsi !== undefined) {
          nextStageY = baseY + delY * (dnsi.position - 1);
        }
        const fullLine = getLineObj(sx, y, columnsX.stages[nextStage.number - 1], nextStageY);
        const nextY = lineSegmentPoint(fullLine, nextX).y;
        if (!stage.isFinal) {
          // Линии
          const bool =
            (nextStage.isFinal && dnsi !== undefined && !dnsi.cla) || (dnsi !== undefined && dnsi.absent) || dsi.absent;
          if (bool) ctx.setLineDash([14, 4]);
          drawLine(sx, y, nextX, nextY, driver.color);
        }
        // Кружочки \ новые гонщики
        drawArc(sx, y, driver.color, dsi.absent);
        // Машинка
        if (STATE.ride.x <= columnsX.stages[nextStage.number - 1]) {
          ctx.drawImage(driver.miniCar, nextX, nextY - carH / 2, carW, carH);
        }
        ctx.setLineDash([]);
      }
    }
    ctx.globalAlpha = 1;
  };
  const drawField = () => {
    const ctx = ctxs.fieldCTX;
    ctx.clearRect(0, 0, size.width, size.height);
    ctx.strokeStyle = "rgba(0,0,0,0.2)";
    ctx.lineWidth = 1;

    translateField(ctx);

    for (const x of columnsX.stages) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, size.fieldHeight);
      ctx.stroke();
    }
    const fx = columnsX.stages[finishNum ? finishNum - 1 : data.stages.length];
    ctx.drawImage(grid, fx - 5, 0);

    drawLines();

    ctx.setTransform(STATE.recording ? 1 : ratio.value, 0, 0, STATE.recording ? 1 : ratio.value, 0, 0);

    ctx.drawImage(shadows.right, 0, 0, 20, size.fieldHeight);
    ctx.drawImage(shadows.left, size.fieldWidth - 20, 0, 20, size.fieldHeight);
  };
  const drawRightCol = () => {
    const ctx = ctxs.rightCTX;
    ctx.clearRect(0, 0, size.width, size.height);
    const nameX = columnsX.rname;
    const posX = columnsX.rpos;
    const ptsX = columnsX.rpts;
    const moveX = columnsX.rmove;
    const noteX = columnsX.rnote;
    ctx.textBaseline = "middle";

    for (let i = 0; i < driversCount; i++) {
      const driver = data.drivers[i];
      let finInfo = driver.stages.find((s) => s.isFinal);
      if (finInfo === undefined) finInfo = driver.stages[driver.stages.length - 1];
      let finPos = finInfo.position;
      const y = Math.round(delY / 2 + delY * (finPos - 1));

      ctx.fillStyle = textColor;
      ctx.globalAlpha = 1;
      setIncludedDriverAlpha(ctx, driver.presentPos, 0.1);

      // Устоновки для появления
      let xOffset = Math.round(presentSettings(ctx, finPos, 100));

      if (driver.finishPos === undefined) {
        ctx.font = `600 ${fontSize}px ${ffr}`;
        ctx.fillText(driver.code, nameX + xOffset, y);
        ctx.fillText(finPos, posX + xOffset, y);
        ctx.fillText("-", ptsX + xOffset, y);
        ctx.fillText("-", moveX + iconSize + 10 + xOffset, y);
        continue;
      }

      ctx.font = `600 ${fontSize}px ${ffr}`;
      ctx.fillText(driver.code, nameX + xOffset, y); // Имя
      ctx.font = `600 ${fontSize}px ${ffl}`;
      ctx.fillText(finInfo.points, ptsX + xOffset, y); // Очки
      if (finInfo.dnf) {
        ctx.fillText("DNF", posX + xOffset, y);
        ctx.fillText("-", moveX + iconSize + 10 + xOffset, y); // Обгонов
      } else if (!finInfo.cla) {
        ctx.fillText("DNC", posX + xOffset, y);
        ctx.fillText("-", moveX + iconSize + 10 + xOffset, y); // Обгонов
      } else {
        ctx.fillText(finPos, posX + xOffset, y); // Позиция
        if (finPos < 4) {
          ctx.drawImage(
            icons.cups[finPos - 1],
            posX + cupSize + xOffset,
            Math.round(y - cupSize / 2),
            cupSize,
            cupSize
          ); // Кубок
        }
        const moveNum = finInfo.move;
        const moveImg = icons.arrows[moveNum > 0 ? 0 : moveNum === 0 ? 1 : 2];
        ctx.drawImage(moveImg, moveX + xOffset, Math.round(y - iconSize / 2), iconSize, iconSize); // Стрелка
        ctx.fillStyle = moveNum > 0 ? "#008d36" : moveNum === 0 ? textColor : "#ea0029";
        ctx.fillText(Math.abs(moveNum).toString(), moveX + iconSize + 10 + xOffset, y); // Обгонов
      }
      ctx.fillStyle = textColor;
      if (finInfo.retirement !== undefined) {
        ctx.drawImage(
          icons[finInfo.retirement.type],
          noteX + xOffset,
          Math.round(y - iconSize / 2),
          iconSize,
          iconSize
        );
        ctx.fillText(finInfo.retirement.text, noteX + iconSize + 10 + xOffset, y);
      } else if (finInfo.fastest !== undefined) {
        ctx.drawImage(icons.fastestLap, noteX + xOffset, Math.round(y - iconSize / 2), iconSize, iconSize);
        ctx.fillText(finInfo.fastest.text, noteX + iconSize + 10 + xOffset, y);
      }
    }
  };
  // const drawGridStageInfo = () => {
  // 	const ctx = ctxs.fullStageInfoCTX
  //
  // 	ctx.globalAlpha = 1
  //
  // 	const x = columnsX.stages[gridNum - 1] - 88
  // 	const blockWidth = 80
  // 	ctx.fillRect(x, 0, blockWidth, size.fieldHeight)
  // 	drawRows(ctx, x, 0, blockWidth, size.fieldHeight)
  //
  // 	for (let i = 0; i < driversCount; i++) {
  // 		const driver = data.drivers[i]
  // 		const info = driver.stages.find(s => s.number === gridNum)
  // 		if (info === undefined) continue
  // 		const y = delY * (info.position - 1) + Math.round(delY / 2)
  // 		const nameX = x + 8
  // 		const posX = nameX + 50
  //
  // 		ctx.fillStyle = textColor
  // 		ctx.textBaseline = 'middle'
  // 		ctx.font = `600 ${fontSize}px ${ffr}`
  //
  // 		// Name
  // 		if (driver.code !== undefined) ctx.fillText(driver.code, nameX, y)
  // 		else ctx.fillText(driver.name, nameX, y)
  //
  // 		ctx.font = `600 ${fontSize}px ${ffl}`
  //
  // 		// Position
  // 		ctx.fillText(info.position, posX, y)
  // 	}
  // }
  const drawFullStageInfo = () => {
    const ctx = ctxs.fullStageInfoCTX;
    ctx.clearRect(0, 0, size.width, size.height);
    if (fullStageInfo === undefined) return;
    // console.log(fullStageInfo)
    translateField(ctx);

    ctx.fillStyle = "#fff";

    // if (fullStageInfo === gridNum) {
    //   drawGridStageInfo()
    //   return;
    // }

    ctx.globalAlpha = 0.8;
    ctx.fillRect(0, 0, size.width, size.fieldHeight);

    ctx.globalAlpha = 1;
    const x = size.windowWidth > 600 ? columnsX.stages[fullStageInfo - 1] - 20 + lColWidth : size.windowWidth - 270;
    const blockWidth = 250;
    ctx.fillRect(x, 0, blockWidth, size.fieldHeight);
    drawRows(ctx, x, 0, blockWidth, size.fieldHeight);

    for (let i = 0; i < driversCount; i++) {
      const driver = data.drivers[i];
      const info = driver.stages.find((s) => s.number === fullStageInfo);
      if (info === undefined || info.absent) continue;
      const y = delY * (info.position - 1) + Math.round(delY / 2);
      const flagX = x + 10;
      const nameX = flagX + flagSize.width + 10;
      const posX = nameX + 50;
      const timeX = posX + 30;
      const lapsX = timeX + 90;

      ctx.fillStyle = textColor;
      ctx.textBaseline = "middle";
      ctx.font = `600 ${fontSize}px ${ffr}`;

      // Flag
      if (driver.flag?.img !== undefined) {
        ctx.drawImage(driver.flag.img, flagX, Math.round(y - flagSize.height / 2), flagSize.width, flagSize.height);
      } else ctx.fillText(driver.flag.code, flagX, y);
      // Name
      if (driver.code !== undefined) ctx.fillText(driver.code, nameX, y);
      else ctx.fillText(driver.name, nameX, y);

      ctx.font = `600 ${fontSize}px ${ffl}`;

      // Position
      ctx.fillText(info.position, posX, y);
      // Time
      if (info.position === 1) ctx.fillText(msToTime(info.time), timeX, y);
      else if (info.gap.laps === 0) ctx.fillText("+" + msToTime(info.gap.time), timeX, y);
      else ctx.fillText("+" + msToTime(info.gap.laps), timeX, y);

      // Laps
      ctx.fillText(info.laps, lapsX, y);
    }

    ctx.drawImage(shadows.left, x - 20, 0, 20, size.height - headHeight);
    ctx.drawImage(shadows.right, x + blockWidth, 0, 20, size.height - headHeight);

    ctx.setTransform(STATE.recording ? 1 : ratio.value, 0, 0, STATE.recording ? 1 : ratio.value, 0, 0);
  };

  const replay = () => {
    resetState();
    if (!STATE.playing) {
      STATE.playing = true;
      playBtnRef.current.checked = true;
    }
  };
  const forcedRedraw = () => {
    drawLeftCol();
    drawRows();
    drawRedHead();
    drawField();
    drawRightCol();
    drawFullStageInfo();
  };
  const draw = () => {
    if (STATE.playing && Date.now() - lastFrameDate > 1000 / 60) {
      updSTATE(STATE.timePassed, STATE);

      drawField();
      drawRightCol();
      drawRedHead();

      STATE.timePassed += Date.now() - lastFrameDate + speed;
      lastFrameDate = Date.now();
    }

    nextFrameID = window.requestAnimationFrame(draw);
  };

  const setContexts = (useRatio = true) => {
    ctxs.leftCTX = createHiDPICanvas(size.width, size.height, leftRef.current, useRatio).getContext("2d");
    ctxs.fieldCTX = createHiDPICanvas(size.fieldWidth, size.fieldHeight, fieldRef.current, useRatio).getContext("2d");
    ctxs.rightCTX = createHiDPICanvas(rColWidth, size.fieldHeight, rightRef.current, useRatio).getContext("2d");
    ctxs.rowsCTX = createHiDPICanvas(size.width, size.height, rowsRef.current, useRatio).getContext("2d");
    ctxs.fullStageInfoCTX = createHiDPICanvas(
      size.width,
      size.fieldHeight,
      fullStageInfoRef.current,
      useRatio
    ).getContext("2d");
    ctxs.redHeadCTX = createHiDPICanvas(size.width, headHeight, redHeadRef.current, useRatio).getContext("2d");
    ctxs.fieldCTX.imageSmoothingEnabled = true;
    for (const ctx of Object.keys(ctxs)) ctxs[ctx].font = `600 ${fontSize}px ${ffl}`;
  };
  useEffect(() => {
    console.log(data);
    window.onresize = () => onResize(updWidth, forcedRedraw);
    updHeight();
    updWidth();
    setFullAnimDur();
    forcedRedraw();
    lastFrameDate = Date.now();
    window.requestAnimationFrame(draw);
    nextFrameID = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(nextFrameID);
  }, []);

  const onClickPlay = () => {
    STATE.playing = !STATE.playing;
    if (STATE.playing) {
      fullStageInfo = undefined;
      forcedRedraw();
    }
    playBtnRef.current.checked = STATE.playing;
    lastFrameDate = Date.now();
  };
  const controlBtnsOptions = {
    replyOnClick: replay,

    plyOnClick: onClickPlay,
    playDef: STATE.playing,
    playRef: playBtnRef,
    moveOnClick: rewind,
    moveFRef: moveForwardRef,
    moveBRef: moveBackRef,
    speedOnClick: changeSpeed,
    speedUpRef: speedUpBtnRef,
    speedDownRef: speedDownBtnRef,

    showZoom: false,
    showType: false,
  };
  return (
    <div style={{ position: "relative" }}>
      <div className={styles.btnsRow}>
        <div className={styles.leftBtns}>
          <ControlButtons options={controlBtnsOptions} />
        </div>
        <div className={styles.rightBtns}>
          <ShareBtns
            title={data.title.name}
            showDownloadBtns={props.isConfig}
            clickOnScreen={downloadScreenshot}
            clickOnVideo={record}
          />
        </div>
      </div>
      <div>
        <div ref={canvasesDivRef} className={styles.canvases} onClick={(e) => clickOnCanvas(e)}>
          <canvas ref={rowsRef} className={styles.canvas + " " + styles.BGRows} />
          <canvas ref={fieldRef} className={styles.canvas} />
          <canvas ref={leftRef} className={styles.canvas} />
          <canvas ref={rightRef} className={styles.canvas} />
          <canvas ref={fullStageInfoRef} className={styles.canvas + " " + styles.FSInfo} />
          <canvas ref={redHeadRef} className={styles.canvas + " " + styles.redHeadCanvas} />
          <div className={styles.dataBy} ref={dataByRef} style={{ background: mainColor }}>
            <DataBy />
          </div>
          <img ref={legendImgRef} className={mainStyles.legendImg} src={legendImg} alt={"Legend"} />
        </div>
        <span ref={recordingSpanRef} className={mainStyles.recordingSpan}>
          Recording
        </span>
      </div>

      {/* legend btn */}
      <button onClick={showLegend} className={mainStyles.legendBtn}>
        KEY
      </button>
      {/* bot social btns */}
      <div className={styles.botSocialBtns}>
        <ShareBtns
          title={data.title.name}
          showDownloadBtns={props.isConfig}
          clickOnScreen={downloadScreenshot}
          clickOnVideo={record}
        />
      </div>

      {/* Customize block */}
      {props.isConfig && (
        <CustomizeBlock def={getThemeData()} onChange={parseCustomizeBlock} getThemeData={getThemeData} />
      )}
    </div>
  );
};

export default Weekend;
